import React, { FC, useState } from "react";

import { startOfWeek, endOfWeek } from "date-fns";

import { formatDateTime } from "~/libs";

import {
  useDateRange,
  useDivisionSectionDropdown,
  useProvidersCurrentEmployee,
  useApisManagersWeeklyReportsCumulativeAnalysisIndex,
  useApisManagersWeeklyReportsPostsRateCalendarsIndex,
  useApisManagersWeeklyReportsFeedbackAnalysisIndex,
  useApisManagersWeeklyReportsEmployeeSurveyProgressesIndex,
  useApisManagersWeeklyReportsEmployeeConditionSurveyProgressesIndex,
  useApisMembersBusinessSectionRelationEmployeesIndex,
  usePrevNextSelectMonth,
} from "~/hooks";

import {
  EmployeeLayout,
  EmployeeLayoutMainContent,
  EmployeeLayoutRightSidebar,
} from "~/components/layouts";
import {
  AnalysisCumulativeOfPostGraph,
  DivisionAndSectionAndEmployeeDropdownField,
  ProgressTableForSurveyProgress,
} from "~/components/organisms";

import { ParentType } from "~/domains";

import { useApisManagersBusinessSectionsLatestBusinessSectionLastWeekPostAiSummariesShow } from "~/features/business-section";
import { CatchTitleBorder, Icon, Loading, NoContentMessage } from "~/ui";
import {
  RangeDatePicker,
  DoughnutChartBox,
  ActivityOriginalCalendar,
} from "~/ui";

const today = new Date();

export const ManagersWeeklyReportsIndex: FC = () => {
  const { currentEmployee } = useProvidersCurrentEmployee();

  const breadCrumbItems = [
    { label: "週次レポート", href: "/managers/weekly_reports" },
    { label: "一覧", href: "/managers/weekly_reports" },
  ];

  const {
    selectableDivisions,
    selectedDivision,
    onDivisionChange,
    optionSelectableSections,
    optionSelectedSection,
    onSectionChange,
  } = useDivisionSectionDropdown({
    selectableDivisions: currentEmployee?.businessDivisions || [],
    selectableSections: currentEmployee?.businessSections || [],
  });

  const [selectedEmployee, setSelectedEmployee] = useState<
    ParentType | undefined
  >();

  const { data: employeesData } =
    useApisMembersBusinessSectionRelationEmployeesIndex({
      params: {
        businessSectionIds: optionSelectedSection
          ? [optionSelectedSection.id]
          : [],
      },
      config: {
        enabled: !!optionSelectedSection,
        onSuccess: (res: {
          employees: ParentType[];
          archivedEmployees: ParentType[];
        }) => {
          if (selectedEmployee) {
            const stillExists = res.employees.some(
              (e) => e.id === selectedEmployee.id,
            );
            if (!stillExists) {
              setSelectedEmployee(undefined);
            }
          }
        },
      },
    });

  const onEmployeeChange = (value: ParentType | undefined) => {
    setSelectedEmployee(value);
  };

  const [
    {
      startDate,
      formattedStartDate,
      endDate,
      formattedEndDate,
      onChange: onChangeDateRange,
    },
  ] = useDateRange([startOfWeek(today), endOfWeek(today)]);

  const [
    {
      startDate: progressStartDate,
      formattedStartDate: progressFormattedStartDate,
      endDate: progressEndDate,
      formattedEndDate: progressFormattedEndDate,
      onChange: onChangeProgressDateRange,
    },
  ] = useDateRange([startOfWeek(today), endOfWeek(today)]);

  const {
    targetDate: calendarTargetDate,
    targetDateString: calendarTargetDateString,
    handlePrevMonthClick,
    handleNextMonthClick,
  } = usePrevNextSelectMonth();

  const commonParams = {
    businessDivisionIds: selectedDivision ? [selectedDivision.id] : [],
    businessSectionIds: optionSelectedSection ? [optionSelectedSection.id] : [],
    employeeIds: selectedEmployee ? [selectedEmployee.id] : undefined,
  };

  const { data: aiSummaryData, isLoading: isAiSummaryLoading } =
    useApisManagersBusinessSectionsLatestBusinessSectionLastWeekPostAiSummariesShow(
      {
        businessSectionId: optionSelectedSection?.id || "",
        config: {
          enabled: !!optionSelectedSection,
        },
      },
    );

  const aiSummary = aiSummaryData?.businessSectionLastWeekPostAiSummary;

  const { data: cumulativeData, isLoading: isCumulativeLoading } =
    useApisManagersWeeklyReportsCumulativeAnalysisIndex({
      params: {
        ...commonParams,
        fromDate: formattedStartDate || undefined,
        toDate: formattedEndDate || undefined,
      },
      config: {
        enabled: !!(formattedStartDate && formattedEndDate),
      },
    });

  const { data: calendarData, isLoading: isCalendarLoading } =
    useApisManagersWeeklyReportsPostsRateCalendarsIndex({
      params: {
        ...commonParams,
        targetDate: calendarTargetDateString,
      },
      config: {
        enabled: !!calendarTargetDateString,
      },
    });

  const { data: feedbackData, isLoading: isFeedbackLoading } =
    useApisManagersWeeklyReportsFeedbackAnalysisIndex({
      params: {
        ...commonParams,
        fromDate: formattedStartDate || undefined,
        toDate: formattedEndDate || undefined,
      },
      config: {
        enabled: !!(formattedStartDate && formattedEndDate),
      },
    });

  const { data: progressesData } =
    useApisManagersWeeklyReportsEmployeeSurveyProgressesIndex({
      params: {
        businessSectionId: optionSelectedSection?.id || "",
        startDate: progressFormattedStartDate || undefined,
        endDate: progressFormattedEndDate || undefined,
      },
      config: {
        enabled: !!(
          progressFormattedStartDate &&
          progressFormattedEndDate &&
          optionSelectedSection
        ),
      },
    });

  const { data: conditionProgressesData } =
    useApisManagersWeeklyReportsEmployeeConditionSurveyProgressesIndex({
      params: {
        businessSectionId: optionSelectedSection?.id || "",
        startDate: progressFormattedStartDate || undefined,
        endDate: progressFormattedEndDate || undefined,
      },
      config: {
        enabled: !!(
          progressFormattedStartDate &&
          progressFormattedEndDate &&
          optionSelectedSection
        ),
      },
    });

  return (
    <EmployeeLayout withRightSidebar breadCrumbItems={breadCrumbItems}>
      <EmployeeLayoutMainContent withRightSidebar>
        <div className="space-y-12">
          <div className="grid miniTablet:grid-cols-2 gap-4">
            <DivisionAndSectionAndEmployeeDropdownField
              selectableDropdownDivisions={selectableDivisions}
              selectedDropdownDivision={selectedDivision}
              onDivisionChange={onDivisionChange}
              selectableDropdownSections={optionSelectableSections}
              selectedDropdownSection={optionSelectedSection}
              onSectionChange={onSectionChange}
              selectableDropdownEmployees={employeesData?.employees}
              selectedDropdownEmployee={selectedEmployee}
              onEmployeeChange={onEmployeeChange}
            />
            <RangeDatePicker
              labelText="期間"
              startDate={startDate}
              endDate={endDate}
              onChange={onChangeDateRange}
              maxDate={today}
            />
          </div>

          <div className="space-y-6">
            <CatchTitleBorder text="かがみんAIの要約" />
            {!optionSelectedSection && (
              <NoContentMessage text="課を選択してください" />
            )}
            {optionSelectedSection && isAiSummaryLoading && <Loading />}
            {optionSelectedSection && !isAiSummaryLoading && aiSummary && (
              <>
                <p className="text-secondary-500 text-center border rounded border-secondary-400 py-2 px-4 flex content-center">
                  <Icon icon="ioCalendarClearOutline" size="1.5rem" />
                  <span className="ml-2">
                    {aiSummary.targetFromDate} 〜 {aiSummary.targetToDate}
                  </span>
                </p>
                <div className="grid miniTablet:grid-cols-2 gap-4">
                  {[
                    {
                      title: "全体の傾向",
                      content: aiSummary.overallTrend,
                    },
                    {
                      title: "ポジティブな特徴",
                      content: aiSummary.positiveCharacteristics,
                    },
                    {
                      title: "模範となる振り返り",
                      content: aiSummary.exemplaryReflections,
                    },
                    {
                      title: "ネガティブな特徴",
                      content: aiSummary.negativeCharacteristics,
                    },
                    {
                      title: "離職アラート",
                      content: aiSummary.turnoverRiskAlert,
                    },
                    {
                      title: "改善のヒント",
                      content: aiSummary.improvementHints,
                    },
                  ].map((item) => (
                    <div
                      key={item.title}
                      className="rounded-md border border-secondary-200 bg-white p-4"
                    >
                      <h4 className="text-sm font-semibold text-primary-800">
                        {item.title}
                      </h4>
                      <p className="mt-2 text-sm text-secondary-600 whitespace-pre-wrap">
                        {item.content}
                      </p>
                    </div>
                  ))}
                </div>
              </>
            )}
            {optionSelectedSection && !isAiSummaryLoading && !aiSummary && (
              <NoContentMessage text="AI要約データがまだありません" />
            )}
          </div>

          <div className="space-y-6">
            <CatchTitleBorder text="累計分析" />
            <div className="grid miniTablet:grid-cols-2 desktop:grid-cols-3 gap-6">
              <AnalysisCumulativeOfPostGraph
                title="メモログ"
                isLoading={isCumulativeLoading}
                postAnalysis={cumulativeData?.insightPostsDailyPostsCounts.map(
                  (item) => ({
                    ...item,
                    name: formatDateTime(item.name, "MM-dd"),
                  }),
                )}
              />
              <AnalysisCumulativeOfPostGraph
                title="チェックアウト"
                isLoading={isCumulativeLoading}
                postAnalysis={cumulativeData?.employeeSurveySubmissionsDailyPostsCounts.map(
                  (item) => ({
                    ...item,
                    name: formatDateTime(item.name, "MM-dd"),
                  }),
                )}
              />
              <AnalysisCumulativeOfPostGraph
                title="コンディション"
                isLoading={isCumulativeLoading}
                postAnalysis={cumulativeData?.employeeConditionSubmissionsDailyPostsCounts.map(
                  (item) => ({
                    ...item,
                    name: formatDateTime(item.name, "MM-dd"),
                  }),
                )}
              />
            </div>
          </div>

          <div className="space-y-6">
            <CatchTitleBorder text="フィードバック分析" />
            <div className="grid miniTablet:grid-cols-2 desktop:grid-cols-3 gap-6">
              <DoughnutChartBox
                title="メモログへのいいね率"
                doughnut={feedbackData?.insightPostLikesDoughnut}
                isLoading={isFeedbackLoading}
              />
              <DoughnutChartBox
                title="メモログへのリプライ率"
                doughnut={feedbackData?.insightPostCommentsDoughnut}
                isLoading={isFeedbackLoading}
              />
              <DoughnutChartBox
                title="チェックアウトへのいいね率"
                doughnut={feedbackData?.employeeSurveySubmissionLikesDoughnut}
                isLoading={isFeedbackLoading}
              />
              <DoughnutChartBox
                title="チェックアウトへのリプライ率"
                doughnut={feedbackData?.employeeSurveySubmissionRepliesDoughnut}
                isLoading={isFeedbackLoading}
              />
              <DoughnutChartBox
                title="コンディションへのいいね率"
                doughnut={
                  feedbackData?.employeeConditionSubmissionLikesDoughnut
                }
                isLoading={isFeedbackLoading}
              />
              <DoughnutChartBox
                title="コンディションへのリプライ率"
                doughnut={
                  feedbackData?.employeeConditionSubmissionRepliesDoughnut
                }
                isLoading={isFeedbackLoading}
              />
            </div>
          </div>

          <div className="space-y-6">
            <CatchTitleBorder text="実施カレンダー" />
            <div className="grid miniTablet:grid-cols-2 desktop:grid-cols-3 gap-6">
              <div>
                <h3 className="text-base font-semibold text-primary-800 text-center">
                  メモログ
                </h3>
                <ActivityOriginalCalendar
                  isLoading={isCalendarLoading}
                  dailyActivity={calendarData?.insightPostsCalendar}
                  targetDate={calendarTargetDate}
                  onPrevMonthClick={handlePrevMonthClick}
                  onNextMonthClick={handleNextMonthClick}
                  isForRate={false}
                />
              </div>
              <div>
                <h3 className="text-base font-semibold text-primary-800 text-center">
                  チェックアウト
                </h3>
                <ActivityOriginalCalendar
                  isLoading={isCalendarLoading}
                  dailyActivity={
                    calendarData?.employeeSurveySubmissionsCalendar
                  }
                  targetDate={calendarTargetDate}
                  onPrevMonthClick={handlePrevMonthClick}
                  onNextMonthClick={handleNextMonthClick}
                  isForRate={false}
                />
              </div>
              <div>
                <h3 className="text-base font-semibold text-primary-800 text-center">
                  コンディション
                </h3>
                <ActivityOriginalCalendar
                  isLoading={isCalendarLoading}
                  dailyActivity={
                    calendarData?.employeeConditionSubmissionsCalendar
                  }
                  targetDate={calendarTargetDate}
                  onPrevMonthClick={handlePrevMonthClick}
                  onNextMonthClick={handleNextMonthClick}
                  isForRate={false}
                />
              </div>
            </div>
          </div>

          <div className="space-y-6">
            <CatchTitleBorder text="投稿一覧" />
            <RangeDatePicker
              labelText="期間"
              startDate={progressStartDate}
              endDate={progressEndDate}
              onChange={onChangeProgressDateRange}
              maxDate={today}
            />

            {optionSelectedSection && (
              <>
                <div className="mt-8">
                  <h3 className="text-lg font-semibold text-primary-800 text-center">
                    チェックアウト投稿一覧
                  </h3>
                  {progressesData?.surveyProgresses.map((surveyProgress) => (
                    <div
                      key={surveyProgress.employeeSurvey.id}
                      className="mt-6"
                    >
                      <h4 className="text-base font-semibold text-secondary-700">
                        {surveyProgress.employeeSurvey.title}
                        <span className="text-sm text-secondary-500 ml-2">
                          ({surveyProgress.employeeSurvey.startDate} 〜{" "}
                          {surveyProgress.employeeSurvey.endDate})
                        </span>
                      </h4>
                      <ProgressTableForSurveyProgress
                        startDate={progressStartDate}
                        endDate={progressEndDate}
                        userProgress={surveyProgress.userProgress}
                      />
                    </div>
                  ))}
                </div>

                <div className="mt-8">
                  <h3 className="text-lg font-semibold text-primary-800 text-center">
                    コンディション投稿一覧
                  </h3>
                  {conditionProgressesData?.surveyProgresses.map(
                    (surveyProgress) => (
                      <div
                        key={surveyProgress.employeeConditionSurvey.id}
                        className="mt-6"
                      >
                        <h4 className="text-base font-semibold text-secondary-700">
                          {surveyProgress.employeeConditionSurvey.title}
                          <span className="text-sm text-secondary-500 ml-2">
                            ({surveyProgress.employeeConditionSurvey.startDate}{" "}
                            〜 {surveyProgress.employeeConditionSurvey.endDate})
                          </span>
                        </h4>
                        <ProgressTableForSurveyProgress
                          startDate={progressStartDate}
                          endDate={progressEndDate}
                          userProgress={surveyProgress.userProgress.map(
                            (user) => ({
                              employee: {
                                id: user.employeeId,
                                name: user.employeeName,
                                avatarUrl: null,
                              },
                              progress: user.progress,
                            }),
                          )}
                        />
                      </div>
                    ),
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </EmployeeLayoutMainContent>
      <EmployeeLayoutRightSidebar />
    </EmployeeLayout>
  );
};
